import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Map } from '@vis.gl/react-maplibre';
import type { MapRef, ViewStateChangeEvent } from '@vis.gl/react-maplibre';

import {
  UnifiedAnchorSymbolLayer,
  TaskGeometryOverlays,
} from '@/components/map/UnifiedAnchorSymbolLayer';
import { useGeoMarkupStore } from '@/store';
import { MAP_CONFIG } from '@/config/maplibre';
import { useBasemapStyle } from '@/hooks/useBasemapStyle';
import { roundZoom } from '@/utils/zoomHelpers';
import { GeometryType } from '@/types/geometry';
import type { Task, Coordinates } from '@/types';

import styles from './TaskMiniMap.module.css';

interface TaskMiniMapProps {
  task: Task;
  className?: string;
}

const SINGLE_POINT_ZOOM = 14;
const FIT_BOUNDS_PADDING = 40;

function collectTaskCoordinates(task: Task): Coordinates[] {
  const coords: Coordinates[] = [];

  if (task.anchorPoint) {
    coords.push(task.anchorPoint);
  }

  for (const geom of task.geometries ?? []) {
    if (geom.calculatedCenter) {
      coords.push(geom.calculatedCenter);
    }
    if (geom.type === GeometryType.Line && geom.linePoints) {
      coords.push(...geom.linePoints);
    }
    if (geom.type === GeometryType.Shape && geom.shapePoints) {
      coords.push(...geom.shapePoints);
    }
  }

  return coords;
}

export const TaskMiniMap = memo(function TaskMiniMap({
  task,
  className,
}: TaskMiniMapProps) {
  const mapRef = useRef<MapRef | null>(null);
  const basemapStyle = useBasemapStyle();
  const [isMapReady, setIsMapReady] = useState(false);
  const [stableZoom, setStableZoom] = useState(SINGLE_POINT_ZOOM);
  const zoomToCoordinates = useGeoMarkupStore(
    (state) => state.map.zoomToCoordinates
  );

  const tasksArray = useMemo(() => [task], [task]);

  const initialViewState = useMemo(() => {
    const anchor = task.anchorPoint;
    return {
      longitude: anchor?.lng ?? MAP_CONFIG.center[0],
      latitude: anchor?.lat ?? MAP_CONFIG.center[1],
      zoom: SINGLE_POINT_ZOOM,
    };
  }, [task.anchorPoint]);

  const fitToTaskBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    const coords = collectTaskCoordinates(task);
    if (coords.length === 0) return;

    if (coords.length === 1 && coords[0]) {
      map.flyTo({
        center: [coords[0].lng, coords[0].lat],
        zoom: SINGLE_POINT_ZOOM,
        duration: 0,
      });
      return;
    }

    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;

    for (const coord of coords) {
      if (coord.lng < minLng) minLng = coord.lng;
      if (coord.lng > maxLng) maxLng = coord.lng;
      if (coord.lat < minLat) minLat = coord.lat;
      if (coord.lat > maxLat) maxLat = coord.lat;
    }

    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: FIT_BOUNDS_PADDING, duration: 0 }
    );
  }, [task]);

  const handleMapLoad = useCallback(() => {
    setIsMapReady(true);
  }, []);

  // Fit bounds on initial load and when task geometries change
  useEffect(() => {
    if (isMapReady) {
      fitToTaskBounds();
    }
  }, [isMapReady, fitToTaskBounds]);

  const handleZoom = useCallback((evt: ViewStateChangeEvent) => {
    setStableZoom(roundZoom(evt.viewState.zoom));
  }, []);

  // Zoom mini-map when store zoomToCoordinates changes (e.g. geometry list click)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady || !zoomToCoordinates) return;

    map.flyTo({
      center: [zoomToCoordinates.lng, zoomToCoordinates.lat],
      zoom: SINGLE_POINT_ZOOM,
      duration: 500,
    });
  }, [zoomToCoordinates, isMapReady]);

  const containerClassName = className
    ? `${styles.container} ${className}`
    : styles.container;

  return (
    <div className={containerClassName}>
      <Map
        ref={mapRef}
        initialViewState={initialViewState}
        onLoad={handleMapLoad}
        onZoom={handleZoom}
        mapStyle={basemapStyle}
        styleDiffing={false}
        maxZoom={MAP_CONFIG.maxZoom}
        minZoom={MAP_CONFIG.minZoom}
        dragPan
        scrollZoom
        dragRotate={false}
        keyboard={false}
        doubleClickZoom={false}
        attributionControl={false}
        // react-map-gl <Map> renders via a non-composable wrapper and only
        // honors inline `style` — it ignores `className` for the canvas
        // container, so full-bleed sizing must stay inline here.
        style={{ width: '100%', height: '100%' }}
      >
        {isMapReady && tasksArray.length > 0 && (
          <>
            <UnifiedAnchorSymbolLayer
              tasks={tasksArray}
              mapRef={mapRef}
              isMapReady={isMapReady}
            />
            <TaskGeometryOverlays tasks={tasksArray} zoom={stableZoom} />
          </>
        )}
      </Map>
    </div>
  );
});
